import { useMemo, useState } from "react";

import { Colors } from "@blueprintjs/core";
import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import StarSVG from "@/../../hypetrain/static/img/star.svg";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { roundToOneDecimalPlace } from "@/components/pages_logged_in/program/components/utils";
import { Avatar } from "@/components/pieces/users/Avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { CARD_CLASSES } from "@/css/constants";
import {
  GetCoachesStatisticsQuery,
  useGetCoachesStatisticsQuery,
} from "@/graphql";
import { MainComponent } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type Coach = NonNullableArrayItem<
  NonNullable<GetCoachesStatisticsQuery["coachesStatistics"]>
>;

interface CoachesTableProps {
  component: MainComponent;
}

export function CoachesTable({ component }: CoachesTableProps) {
  const { data, loading } = useGetCoachesStatisticsQuery({
    variables: {
      programComponentId: component.id,
    },
  });

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
  ]);

  const coaches = useMemo(() => {
    return ensureNonNullArray(data?.coachesStatistics);
  }, [data?.coachesStatistics]);

  const columns: ColumnDef<Coach>[] = [
    {
      id: "name",
      header: ({ column }: { column: Column<Coach, unknown> }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      accessorKey: "name",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const nameA = rowA.original.name?.toLowerCase() || "";
        const nameB = rowB.original.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      cell: ({ row }: { row: Row<Coach> }) => {
        const { name, photo } = row.original;
        return (
          <Flex alignItems="center">
            <Box mr={2}>
              <Avatar
                size={20}
                user={{
                  name,
                  photo: photo,
                }}
              />
            </Box>
            <Box style={{ fontSize: 16 }}>{name}</Box>
          </Flex>
        );
      },
    },
    {
      id: "activeParticipantsCount",
      header: ({ column }: { column: Column<Coach, unknown> }) => (
        <DataTableColumnHeader column={column} title="Active Participants" />
      ),
      accessorKey: "activeParticipantsCount",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const countA = rowA.original.activeParticipantsCount || 0;
        const countB = rowB.original.activeParticipantsCount || 0;
        return countA - countB;
      },
      cell: ({ row }: { row: Row<Coach> }) => {
        return (
          <Flex flexDirection="column">
            <span style={{ fontSize: 16, fontWeight: 500 }}>
              {row.original.activeParticipantsCount || "0"}
            </span>
            <span style={{ color: "#778397", fontSize: 14 }}>participants</span>
          </Flex>
        );
      },
    },
    {
      id: "sessionDeliveredCount",
      header: ({ column }: { column: Column<Coach, unknown> }) => (
        <DataTableColumnHeader column={column} title="Sessions Delivered" />
      ),
      accessorKey: "sessionDeliveredCount",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const countA = rowA.original.sessionDeliveredCount || 0;
        const countB = rowB.original.sessionDeliveredCount || 0;
        return countA - countB;
      },
      cell: ({ row }: { row: Row<Coach> }) => {
        return (
          <Flex flexDirection="column">
            <span style={{ fontSize: 16, fontWeight: 500 }}>
              {row.original.sessionDeliveredCount || "0"}
            </span>
            <span style={{ color: "#778397", fontSize: 14 }}>sessions</span>
          </Flex>
        );
      },
    },
    {
      id: "creditSpent",
      header: ({ column }: { column: Column<Coach, unknown> }) => (
        <DataTableColumnHeader column={column} title="Credits Spent" />
      ),
      accessorKey: "creditSpent",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const creditA = rowA.original.creditSpent || 0;
        const creditB = rowB.original.creditSpent || 0;
        return creditA - creditB;
      },
      cell: ({ row }: { row: Row<Coach> }) => {
        return (
          <Flex style={{ gap: 8 }} alignItems="center">
            <EmbedSvg src={CreditSVG} height={16} width={16} />
            <span style={{ fontSize: 16 }}>
              {roundToOneDecimalPlace(row.original.creditSpent || 0)}
            </span>
          </Flex>
        );
      },
    },
    {
      id: "averageRating",
      header: ({ column }: { column: Column<Coach, unknown> }) => (
        <DataTableColumnHeader column={column} title="Average Rating" />
      ),
      accessorKey: "averageRating",
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const ratingA = rowA.original.averageRating || 0;
        const ratingB = rowB.original.averageRating || 0;
        return ratingA - ratingB;
      },
      cell: ({ row }: { row: Row<Coach> }) => {
        return (
          <Flex style={{ gap: 8 }} alignItems="center">
            <EmbedSvg src={StarSVG} height={16} width={16} />
            <span style={{ fontSize: 16, fontWeight: "bold" }}>
              {row.original.averageRating
                ? roundToOneDecimalPlace(row.original.averageRating)
                : "-"}
            </span>
            <span style={{ color: "#778397", fontSize: 14 }}>
              ({row.original.ratingCount || "0"})
            </span>
          </Flex>
        );
      },
    },
  ];

  const table = useReactTable({
    data: coaches,
    columns,
    state: {
      sorting,
    },
    enableSorting: true,
    enableHiding: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row?.id || "",
  });

  const loadingSkeleton = (
    <div className="flex flex-col gap-4 h-full w-full">
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
    >
      <Box fontSize={18} fontWeight={600} mb={24}>
        Coaches
      </Box>

      <DataTable
        table={table}
        className="overflow-visible"
        pagination={false}
        emptyState={
          <div className="flex flex-col items-center justify-center py-8">
            {loading ? (
              loadingSkeleton
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-500">
                No coaches
              </div>
            )}
          </div>
        }
      />
    </Box>
  );
}
